import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { Icon } from '@/components/ui/Icon';
import { Tooltip } from '@/components/ui/Tooltip';
import { ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';

/**
 * QuickAddMenu Component
 *
 * Reusable quick action menu for adding content to the calendar.
 * Shows a plus icon that expands into a dropdown with options:
 * - Create Post (only for today or future dates)
 * - Create Campaign
 * - Create Content Pillar
 */

interface QuickAddMenuProps {
  /**
   * The date this menu is for
   */
  date: Date;

  /**
   * Callback when add post is clicked
   */
  onAddPost?: (date: Date) => void;

  /**
   * Callback when add campaign is clicked
   */
  onAddCampaign?: (date: Date) => void;

  /**
   * Callback when add content pillar is clicked
   */
  onAddContentPillar?: (date: Date) => void;

  /**
   * Additional class names for the container
   */
  className?: string;

  /**
   * Position of the dropdown menu
   */
  menuPosition?: 'left' | 'right' | 'center';

  /**
   * Whether to show on hover (requires parent to have 'group' class)
   */
  showOnHover?: boolean;

  /**
   * Force the menu to always be visible (not just on hover)
   */
  alwaysVisible?: boolean;
}

export const QuickAddMenu = ({
  date,
  onAddPost,
  onAddCampaign,
  onAddContentPillar,
  className,
  menuPosition = 'right',
  showOnHover = true,
  alwaysVisible = false,
}: QuickAddMenuProps) => {
  const { t } = useTranslation('common');
  const [showMenu, setShowMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Check if date is today or in the future
  const isPostAllowed = date >= new Date(new Date().setHours(0, 0, 0, 0));

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMenu]);

  // Don't render if no callbacks provided
  if (!onAddPost && !onAddCampaign && !onAddContentPillar) {
    return null;
  }

  const menuPositionClasses = {
    left: 'left-0',
    right: 'right-0',
    center: 'left-1/2 -translate-x-1/2',
  };

  return (
    <div
      className={cn('relative', className)}
      ref={menuRef}
      onKeyDown={(e) => {
        // Escape from the trigger OR any menu item closes the menu, so aria-expanded never stays "true"
        // (shouldYieldEscape trusts it and would otherwise block Escape from closing the surrounding modal).
        if (e.key === 'Escape' && showMenu) {
          // Consume the key so enclosing escape layers don't also close, and hand focus back to the trigger.
          e.preventDefault();
          setShowMenu(false);
          triggerRef.current?.focus();
        }
      }}
    >
      <Tooltip content={t('add_post_campaign_pillar', { defaultValue: 'Add post/campaign/content pillar' })}>
        <button
          ref={triggerRef}
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowMenu(!showMenu);
          }}
          className={cn(
            'transition-opacity duration-150',
            'text-gray-400 hover:text-primary-500',
            'focus:opacity-100 focus:outline-none',
            showOnHover && !alwaysVisible && 'lg:opacity-0 lg:group-hover:opacity-100',
            alwaysVisible && 'opacity-100',
            showMenu && 'opacity-100 text-primary-500'
          )}
          aria-label={t('add_post_campaign_pillar', { defaultValue: 'Add post/campaign/content pillar' })}
          aria-expanded={showMenu}
          aria-haspopup="menu"
        >
          <FontAwesomeIcon icon={faPlus} className="text-xs" />
        </button>
      </Tooltip>

      {/* Dropdown menu */}
      {showMenu && (
        <div
          className={cn(
            'absolute top-full mt-1 z-50',
            'bg-white rounded-lg shadow-lg border border-gray-200 py-1 whitespace-nowrap',
            menuPositionClasses[menuPosition]
          )}
          role="menu"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Create Post - only for today or future dates */}
          {onAddPost && isPostAllowed && (
            <button
              type="button"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 sm:gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
                onAddPost(date);
              }}
              role="menuitem"
            >
              <Icon name={ICONS.FILE} size="xs" className="text-gray-400" ariaHidden />
              {t('create_post', { defaultValue: 'Create Post' })}
            </button>
          )}

          {/* Create Campaign */}
          {onAddCampaign && (
            <button
              type="button"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 sm:gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
                onAddCampaign(date);
              }}
              role="menuitem"
            >
              <Icon name={ICONS.FLAG} size="xs" className="text-gray-400" ariaHidden />
              {t('create_campaign', { defaultValue: 'Campaign' })}
            </button>
          )}

          {/* Create Content Pillar */}
          {onAddContentPillar && (
            <button
              type="button"
              className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-left text-xs sm:text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 sm:gap-2"
              onClick={(e) => {
                e.stopPropagation();
                setShowMenu(false);
                onAddContentPillar(date);
              }}
              role="menuitem"
            >
              <Icon name={ICONS.REFRESH} size="xs" className="text-gray-400" ariaHidden />
              {t('create_content_pillar', { defaultValue: 'Pillar' })}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default QuickAddMenu;
